import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const token = localStorage.getItem('token')

const deriveApprovalStatus = (car) => {
    if (!car || typeof car !== 'object') return 'pending';
    const statusValue =
        car.approvalStatus ??
        car.status ??
        car.approval_status ??
        car.approvalState ??
        car?.submission?.formStatus;

    if (statusValue === null || statusValue === undefined) {
        return 'pending';
    }

    if (typeof statusValue === 'string') {
        return statusValue.toLowerCase().trim() || 'pending';
    }

    return String(statusValue).toLowerCase().trim() || 'pending';
};

const filterCarsByStatus = (cars = [], status = 'all') => {
    if (!status || status === 'all') {
        return cars || [];
    }

    return (cars || []).filter((car) => deriveApprovalStatus(car) === status);
};

// Example async thunk for submitting a blog post
export const submitAnnouncementPost = createAsyncThunk(
    'blog/submitAnnouncementPost',
    async (blogData, thunkAPI) => {
        // Replace with your API call
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/announcement`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(blogData),
        });
        if (!response.ok) {
            throw new Error('Failed to submit blog post');
        }
        return await response.json();
    }
);

export const submitUpdatePost = createAsyncThunk(
    'blog/submitUpdatePost',
    async ({ id, updatedData }, thunkAPI) => {
        console.log('...updating blog post in blogSlice....', id, JSON.stringify(updatedData))
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/announcement/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
              },
            body: JSON.stringify(updatedData),
        });
        if (!response.ok) {
            throw new Error('Failed to update announcement post');
        }
        return await response.json();
    }
);

export const deleteCar = createAsyncThunk(
    'blog/deleteCar',
    async (id, thunkAPI) => {
        console.log('...deleting car ....', id);
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/list_your_car/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
        });
        if (!response.ok) {
            throw new Error('Failed to delete car');
        }
        return await response.json();
    }
);

export const fetchCarListings = createAsyncThunk(
    'blog/fetchCarListings',
    async ({ page = 1, limit = 10, status = 'all', approvalStatus = null, search = '' } = {}, thunkAPI) => {
        const normalizedSearch = typeof search === 'string' ? search.trim() : '';
        const normalizedStatus = approvalStatus && approvalStatus !== 'all'
            ? approvalStatus
            : status;

        let url;
        let params;

        if (normalizedSearch) {
            // Use the dedicated search endpoint when a query is present
            params = new URLSearchParams({
                q: normalizedSearch,
                page: String(page),
                limit: String(limit),
            });

            if (normalizedStatus && normalizedStatus !== 'all' && normalizedStatus !== 'rejected') {
                params.set('status', normalizedStatus);
            }

            url = `${process.env.REACT_APP_BASE_URL}/api/list_your_car/search?${params.toString()}`;
        } else if (approvalStatus === 'rejected') {
            // Use specific endpoint for rejected listings
            params = new URLSearchParams({ page: String(page), limit: String(limit) });
            url = `${process.env.REACT_APP_BASE_URL}/api/list_your_car/rejected?${params.toString()}`;
        } else {
            // Use regular endpoint with status parameter
            params = new URLSearchParams({ page: String(page), limit: String(limit), status });
            url = `${process.env.REACT_APP_BASE_URL}/api/list_your_car?${params.toString()}`;
        }

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error('Failed to fetch car listings');
        }
        const json = await response.json();
        const data = json?.data || {};
        let cars = data.cars || [];
        let effectiveMatchType = data.matchType ?? null;
        let effectiveSearchQuery = data.searchQuery ?? normalizedSearch;
        let effectiveTotal = data.total ?? cars.length;
        let effectiveCount = data.count ?? cars.length;
        let effectiveTotalPages = data.totalPages ?? 1;
        let effectiveCurrentPage = data.currentPage ?? page;

        let filteredCars = filterCarsByStatus(cars, normalizedStatus);

        if (normalizedSearch && normalizedStatus === 'rejected' && filteredCars.length === 0) {
            const fallbackParams = new URLSearchParams({
                q: normalizedSearch,
                page: String(page),
                limit: String(limit),
            });

            const fallbackUrl = `${process.env.REACT_APP_BASE_URL}/api/list_your_car/search?${fallbackParams.toString()}`;
            const fallbackResponse = await fetch(fallbackUrl);

            if (fallbackResponse.ok) {
                const fallbackJson = await fallbackResponse.json();
                const fallbackData = fallbackJson?.data || {};
                const fallbackCars = filterCarsByStatus(fallbackData.cars || [], normalizedStatus);

                filteredCars = fallbackCars;
                effectiveMatchType = fallbackData.matchType ?? effectiveMatchType ?? null;
                effectiveSearchQuery = fallbackData.searchQuery ?? normalizedSearch;
                effectiveTotal = fallbackData.total ?? filteredCars.length;
                effectiveCount = fallbackCars.length;
                effectiveTotalPages = fallbackData.totalPages ?? effectiveTotalPages ?? 1;
                effectiveCurrentPage = fallbackData.currentPage ?? page;
            }
        }

        if (normalizedStatus && normalizedStatus !== 'all') {
            effectiveTotal = filteredCars.length;
            effectiveCount = filteredCars.length;
        } else {
            effectiveTotal = effectiveTotal ?? filteredCars.length;
            effectiveCount = effectiveCount ?? filteredCars.length;
        }

        return {
            cars: filteredCars,
            totalPages: effectiveTotalPages,
            currentPage: effectiveCurrentPage,
            total: effectiveTotal,
            count: effectiveCount,
            searchQuery: effectiveSearchQuery,
            matchType: effectiveMatchType,
        };
    }
);

export const updateCarListingStatus = createAsyncThunk(
    'blog/updateCarListingStatus',
    async ({ id, status }, thunkAPI) => {
      const token = localStorage.getItem('token'); // ✅ ALWAYS fresh
  
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/list_your_car/${id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          credentials: 'include', // ✅ VERY IMPORTANT
          body: JSON.stringify({ status }),
        }
      );
  
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to update car listing status');
      }
  
      const json = await response.json();
      return json.data;
    }
  );
  
// export const updateCarListingStatus = createAsyncThunk(
//     'blog/updateCarListingStatus',
//     async ({ id, action }, thunkAPI) => {
//         const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/list_your_car/${id}`, {
//             method: 'PUT',
//             headers: {
//                 'Content-Type': 'application/json',
//                 ...(token && { Authorization: `Bearer ${token}` }),
//               },
//             body: JSON.stringify({ approvalStatus: action.approvalStatus }),
//         });
//         if (!response.ok) {
//             throw new Error('Failed to update car listing status');
//         }
//         const { data } = await response.json();
//         return data?.cars;
//     }
// );

export const updateCarListingPrice = createAsyncThunk(
    'blog/updateCarListingPrice',
    async ({ id, price }, thunkAPI) => {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/list_your_car/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
              },
            body: JSON.stringify({ askingPrice: price }),
        });
        if (!response.ok) {
            throw new Error('Failed to update car listing price');
        }
        const { data } = await response.json();
        return data?.cars;
    }
);

export const fetchSingleCarListing = createAsyncThunk(
    'blog/fetchSingleCarListing',
    async (id, thunkAPI) => {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/list_your_car/${id}`);
        if (!response.ok) {
            throw new Error('Failed to fetch car listing');
        }
        const json = await response.json();
        const data = json?.data || {};
        return data.car || data;
    }
);

export const updateCarListing = createAsyncThunk(
    'blog/updateCarListing',
    async ({ id, updatedData }, thunkAPI) => {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/list_your_car/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
            },
            body: JSON.stringify(updatedData),
        });
        if (!response.ok) {
            throw new Error('Failed to update car listing');
        }
        const { data } = await response.json();
        return data?.car || data?.cars;
    }
);

export const uploadInspectReport = createAsyncThunk(
    'blog/uploadInspectReport',
    async ({ listingId, file }, thunkAPI) => {
        try {
            // First upload the PDF to AWS S3
            const formData = new FormData();
            formData.append('inspectReport', file);
            
            const authToken = localStorage.getItem('token');
            const uploadResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/api/upload`, {
                method: 'POST',
                // Do not set Content-Type so browser adds proper multipart boundary
                headers: {
                    ...(authToken && { Authorization: `Bearer ${authToken}` }),
                },
                body: formData,
            });
            
            if (!uploadResponse.ok) {
                throw new Error('Failed to upload PDF to AWS S3');
            }
            
            const uploadResult = await uploadResponse.json();

            // Extract URL and filename defensively (API response may vary)
            const pdfUrl = uploadResult?.imageUrl 
                || uploadResult?.url 
                || uploadResult?.fileUrl 
                || uploadResult?.files?.[0]?.url 
                || uploadResult?.uploaded?.[0]?.url;
            const pdfName = uploadResult?.originalFilename 
                || uploadResult?.originalname 
                || uploadResult?.filename 
                || uploadResult?.files?.[0]?.originalname 
                || file?.name;

            // Ensure we have a URL before proceeding
            if (!pdfUrl) {
                throw new Error('Upload succeeded but no URL returned from server');
            }

            // Then update the car listing with the PDF URL
            const updateResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/api/list_your_car/${listingId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authToken && { Authorization: `Bearer ${authToken}` }),
                },
                body: JSON.stringify({ 
                    // Use backend-expected field so MongoDB persists it
                    inspectionReportLink: pdfUrl
                }),
            });
            
            if (!updateResponse.ok) {
                throw new Error('Failed to update car listing with PDF URL');
            }
            
            const updateResult = await updateResponse.json();
            return {
                listingId,
                pdfUrl,
                pdfName,
                updatedListing: updateResult.data
            };
        } catch (error) {
            throw new Error(`PDF upload failed: ${error.message}`);
        }
    }
);

const initialState = {
    title: '',
    content: '',
    items: [],          // ⬅️ stores fetched cars
    selectedCar: null,  // ⬅️ stores single car for detail view
    status: 'idle',
    error: null,
    currentPage: 1,
    totalPages: 1,
    total: 0,
    hasMore: false,
    searchQuery: '',
    matchType: null,
    count: 0,
};


const announcementSlice = createSlice({
    name: 'announcement',
    initialState,
    reducers: {
        setTitle(state, action) {
            state.title = action.payload;
        },
        setContent(state, action) {
            state.content = action.payload;
        },
        resetAnnouncement(state) {
            state.title = '';
            state.content = '';
            state.status = 'idle';
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(submitAnnouncementPost.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(submitAnnouncementPost.fulfilled, (state) => {
                state.status = 'succeeded';
            })
            .addCase(submitAnnouncementPost.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(fetchCarListings.pending, (state, action) => {
                state.status = 'loading';
                // Clear items when starting a new fetch (page 1) to show loading state
                const requestedPage = action.meta?.arg?.page ?? 1;
                if (requestedPage === 1) {
                    state.items = [];
                    state.searchQuery = action.meta?.arg?.search?.trim?.() || '';
                    state.matchType = null;
                    state.count = 0;
                }
            })
            .addCase(fetchCarListings.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const { cars, totalPages, currentPage, total, count, searchQuery, matchType } = action.payload || {};
                const requestedPage = action.meta?.arg?.page ?? 1;
                if (requestedPage > 1) {
                    state.items = [...state.items, ...(cars || [])];
                } else {
                    state.items = cars || [];
                }
                state.currentPage = currentPage || requestedPage;
                state.totalPages = totalPages || 1;
                state.total = total || state.items.length;
                state.hasMore = (state.currentPage < state.totalPages);
                state.count = count ?? state.items.length;
                state.searchQuery = searchQuery ?? action.meta?.arg?.search ?? '';
                state.matchType = matchType ?? null;
            })
            .addCase(fetchCarListings.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
                state.matchType = null;
            })
            .addCase(updateCarListingStatus.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(updateCarListingStatus.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = action.payload;
            })
            .addCase(updateCarListingStatus.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(updateCarListingPrice.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(updateCarListingPrice.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = action.payload;
            })
            .addCase(updateCarListingPrice.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(uploadInspectReport.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(uploadInspectReport.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Update the specific listing in the items array
                const listingIndex = state.items.findIndex(item => item._id === action.payload.listingId);
                if (listingIndex !== -1) {
                    state.items[listingIndex] = {
                        ...state.items[listingIndex],
                        // Persisted field from backend
                        inspectionReportLink: action.payload.pdfUrl,
                        // UI convenience fields
                        inspectReportUrl: action.payload.pdfUrl,
                        inspectReportName: action.payload.pdfName
                    };
                }
            })
            .addCase(uploadInspectReport.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(fetchSingleCarListing.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchSingleCarListing.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedCar = action.payload;
                // Check if we have this car in items array with populated userId
                const existingCar = state.items.find(item => item._id === fetchedCar?._id);
                // If userId in fetched car is just a string (ID) but we have populated userId in items, use it
                if (fetchedCar && existingCar && existingCar.userId && typeof existingCar.userId === 'object') {
                    // Preserve the populated userId from items array
                    state.selectedCar = {
                        ...fetchedCar,
                        userId: existingCar.userId
                    };
                } else {
                    state.selectedCar = fetchedCar;
                }
            })
            .addCase(fetchSingleCarListing.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(updateCarListing.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(updateCarListing.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const updatedCar = action.payload;
                // Only update if we have a valid car object with _id
                if (updatedCar && updatedCar._id) {
                    // Update in items array if it exists
                    const index = state.items.findIndex(item => item._id === updatedCar._id);
                    if (index !== -1) {
                        state.items[index] = updatedCar;
                    }
                    // Update selectedCar if it's the same
                    if (state.selectedCar && state.selectedCar._id === updatedCar._id) {
                        state.selectedCar = updatedCar;
                    }
                }
            })
            .addCase(updateCarListing.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { setTitle, setContent, resetAnnouncement } = announcementSlice.actions;

export default announcementSlice.reducer;
